/**
 * Inline SVG icon set.
 *
 * Icons are returned as detached SVG elements rather than markup strings so
 * they can be inserted with `append()` without ever touching `innerHTML`.
 * All icons share a 24x24 viewBox, `currentColor` strokes and a 1.7 stroke
 * width so they sit consistently beside 14px label text.
 */

import { svgEl } from './dom.js';

const PATHS = {
  logo: [
    'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17Z',
    'M12 7.6V12l3.1 1.9',
  ],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 7.2V12l3.2 2'],
  calendar: [
    'M4.5 6.8A1.8 1.8 0 0 1 6.3 5h11.4a1.8 1.8 0 0 1 1.8 1.8v11.4a1.8 1.8 0 0 1-1.8 1.8H6.3a1.8 1.8 0 0 1-1.8-1.8V6.8Z',
    'M8.2 3.2v3.4M15.8 3.2v3.4M4.5 10h15',
  ],
  upload: ['M12 16.5V4.2', 'M7.6 8.6 12 4.2l4.4 4.4', 'M4.5 15v3.3a1.7 1.7 0 0 0 1.7 1.7h11.6a1.7 1.7 0 0 0 1.7-1.7V15'],
  sparkles: [
    'M12 3.2 13.6 8l4.8 1.6-4.8 1.6L12 16l-1.6-4.8L5.6 9.6 10.4 8 12 3.2Z',
    'M18.6 15.4l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z',
  ],
  settings: [
    'M12 15.1a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z',
    'M19.3 14.2a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-2.6 1.1v.3a1.8 1.8 0 1 1-3.6 0v-.2a1.5 1.5 0 0 0-2.7-1.1l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0-1.1-2.6h-.3a1.8 1.8 0 1 1 0-3.6h.2a1.5 1.5 0 0 0 1.1-2.7l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3h.1a1.5 1.5 0 0 0 .9-1.4v-.3a1.8 1.8 0 1 1 3.6 0v.2a1.5 1.5 0 0 0 2.6 1.1l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7v.1a1.5 1.5 0 0 0 1.4.9h.3a1.8 1.8 0 1 1 0 3.6h-.2a1.5 1.5 0 0 0-1.4.9Z',
  ],
  users: [
    'M15.8 20v-1.7a3.4 3.4 0 0 0-3.4-3.4H6.6a3.4 3.4 0 0 0-3.4 3.4V20',
    'M9.5 11.5a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z',
    'M20.8 20v-1.7a3.4 3.4 0 0 0-2.6-3.3M15.6 4.8a3.4 3.4 0 0 1 0 6.6',
  ],
  lock: [
    'M6.6 10.8h10.8a1.6 1.6 0 0 1 1.6 1.6v6.2a1.6 1.6 0 0 1-1.6 1.6H6.6A1.6 1.6 0 0 1 5 18.6v-6.2a1.6 1.6 0 0 1 1.6-1.6Z',
    'M8.4 10.8V7.6a3.6 3.6 0 1 1 7.2 0v3.2',
  ],
  unlock: [
    'M6.6 10.8h10.8a1.6 1.6 0 0 1 1.6 1.6v6.2a1.6 1.6 0 0 1-1.6 1.6H6.6A1.6 1.6 0 0 1 5 18.6v-6.2a1.6 1.6 0 0 1 1.6-1.6Z',
    'M8.4 10.8V7.6a3.6 3.6 0 0 1 6.9-1.3',
  ],
  warning: ['M12 4.2 2.9 19.4h18.2L12 4.2Z', 'M12 10v4', 'M12 17.1h.01'],
  info: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 16v-4.4', 'M12 8.1h.01'],
  check: ['M4.8 12.6l4.6 4.6L19.2 7.4'],
  close: ['M6 6l12 12M18 6L6 18'],
  chevronLeft: ['M14.5 5.5 8 12l6.5 6.5'],
  chevronRight: ['M9.5 5.5 16 12l-6.5 6.5'],
  chevronDown: ['M6 9.5 12 15.5l6-6'],
  plus: ['M12 5v14M5 12h14'],
  trash: ['M4.5 7.1h15', 'M9.2 7.1V5.4a1.4 1.4 0 0 1 1.4-1.4h2.8a1.4 1.4 0 0 1 1.4 1.4v1.7', 'M6.6 7.1l.8 11.6a1.6 1.6 0 0 0 1.6 1.5h6a1.6 1.6 0 0 0 1.6-1.5l.8-11.6'],
  copy: [
    'M9.4 9.4h8.4a1.6 1.6 0 0 1 1.6 1.6v8.4a1.6 1.6 0 0 1-1.6 1.6H9.4a1.6 1.6 0 0 1-1.6-1.6V11a1.6 1.6 0 0 1 1.6-1.6Z',
    'M4.6 14.6A1.6 1.6 0 0 1 3 13V4.6A1.6 1.6 0 0 1 4.6 3H13a1.6 1.6 0 0 1 1.6 1.6',
  ],
  download: ['M12 4.2v12.3', 'M7.6 12.1 12 16.5l4.4-4.4', 'M4.5 15v3.3a1.7 1.7 0 0 0 1.7 1.7h11.6a1.7 1.7 0 0 0 1.7-1.7V15'],
  print: [
    'M7 9.2V3.8h10v5.4',
    'M7 17.6H5.4A1.6 1.6 0 0 1 3.8 16v-4.4a1.6 1.6 0 0 1 1.6-1.6h13.2a1.6 1.6 0 0 1 1.6 1.6V16a1.6 1.6 0 0 1-1.6 1.6H17',
    'M7 14.4h10v5.8H7v-5.8Z',
  ],
  grid: ['M4 4h7v7H4V4Z', 'M13 4h7v7h-7V4Z', 'M4 13h7v7H4v-7Z', 'M13 13h7v7h-7v-7Z'],
  timeline: ['M3.5 7h17', 'M3.5 12h11', 'M3.5 17h14', 'M17.5 5v4M11 10v4M20 15v4'],
  table: ['M4 5.5h16v13H4v-13Z', 'M4 10h16', 'M9.5 10v8.5'],
  sun: ['M12 16.6a4.6 4.6 0 1 0 0-9.2 4.6 4.6 0 0 0 0 9.2Z', 'M12 2.4v2M12 19.6v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.4 12h2M19.6 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4'],
  moon: ['M20.4 13.4A8.4 8.4 0 1 1 10.6 3.6a6.6 6.6 0 0 0 9.8 9.8Z'],
  cloud: ['M6.8 18.4h10.6a3.9 3.9 0 0 0 .5-7.7 5.6 5.6 0 0 0-10.7-1.5 4.6 4.6 0 0 0-.4 9.2Z'],
  drag: ['M9 6.5h.01M9 12h.01M9 17.5h.01M15 6.5h.01M15 12h.01M15 17.5h.01'],
  refresh: ['M20 11.6A8 8 0 0 0 6.2 6.8L3.5 9.3', 'M4 12.4a8 8 0 0 0 13.8 4.8l2.7-2.5', 'M3.5 4.6v4.7h4.7M20.5 19.4v-4.7h-4.7'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z', 'M20 20l-4-4'],
  file: ['M13.6 3.5H7.4a1.8 1.8 0 0 0-1.8 1.8v13.4a1.8 1.8 0 0 0 1.8 1.8h9.2a1.8 1.8 0 0 0 1.8-1.8V8.3l-4.8-4.8Z', 'M13.4 3.6v4.9h4.9'],
  reset: ['M3.6 5.4v4.7h4.7', 'M4.9 15a8 8 0 1 0 1.4-8.2L3.6 10.1'],
  help: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M9.6 9.5a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.8-.9 1.4v.5', 'M12 17.1h.01'],
};

/** Icons whose paths are meant to be filled rather than stroked. */
const FILLED = new Set([]);

/**
 * Build an icon element.
 *
 * @param {string} name - key from the set above
 * @param {object} [options]
 * @param {number} [options.size=18]
 * @param {string} [options.className]
 * @param {string} [options.title] - when given the icon becomes `role="img"`
 *   with an accessible name; otherwise it is `aria-hidden` decoration.
 */
export function icon(name, { size = 18, className = '', title = '' } = {}) {
  const paths = PATHS[name] ?? PATHS.info;
  const svg = svgEl(
    'svg',
    {
      class: ['icon', className].filter(Boolean).join(' '),
      viewBox: '0 0 24 24',
      width: size,
      height: size,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.7,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      focusable: 'false',
      'aria-hidden': title ? null : 'true',
      role: title ? 'img' : null,
    },
    [
      title ? svgEl('title', {}, title) : null,
      ...paths.map((d) => svgEl('path', { d, fill: FILLED.has(name) ? 'currentColor' : 'none' })),
    ].filter(Boolean),
  );
  return svg;
}

/** True when the name exists - lets callers fail loudly in development. */
export function hasIcon(name) {
  return Object.hasOwn(PATHS, name);
}

/**
 * The Lunchly wordmark lockup: the clock/fork monogram plus the name.
 * Used in the top bar and on the printable export.
 */
export function brandMark({ size = 30 } = {}) {
  return svgEl(
    'svg',
    {
      class: 'brand-mark',
      viewBox: '0 0 64 64',
      width: size,
      height: size,
      role: 'img',
      'aria-label': 'Lunchly',
    },
    [
      svgEl('defs', {}, [
        svgEl('linearGradient', { id: 'brand-mark-bg', x1: '0', y1: '0', x2: '1', y2: '1' }, [
          svgEl('stop', { offset: '0', 'stop-color': '#14a58f' }),
          svgEl('stop', { offset: '1', 'stop-color': '#0c6a5f' }),
        ]),
      ]),
      svgEl('rect', { width: 64, height: 64, rx: 15, fill: 'url(#brand-mark-bg)' }),
      svgEl('circle', {
        cx: 32, cy: 32, r: 17.5, fill: 'none', stroke: '#ffffff', 'stroke-width': 3.2, opacity: 0.95,
      }),
      svgEl('path', {
        d: 'M32 21.5V32l7.5 4.6',
        fill: 'none',
        stroke: '#ffffff',
        'stroke-width': 3.2,
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      }),
      svgEl(
        'g',
        { stroke: '#ffa11f', 'stroke-width': 2.6, 'stroke-linecap': 'round', fill: 'none' },
        [
          svgEl('path', { d: 'M17 12v8' }),
          svgEl('path', { d: 'M22 12v8' }),
          svgEl('path', { d: 'M19.5 20v-0.2a4.6 4.6 0 0 0 2.5-4.1V12' }),
          svgEl('path', { d: 'M19.5 20.5V29' }),
        ],
      ),
      svgEl('circle', { cx: 32, cy: 32, r: 2.6, fill: '#ffa11f' }),
    ],
  );
}
